import { useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../store/useSessionStore";
import { usePartnerStore } from "../store/usePartnerStore";
import { useStore } from "../store/globalState";
import { RequestStatus, LINK_ERROR_CODE } from "../utils/commons";
import { claimLink, LinkClaimError, setWidgetLinkId } from "../services/api";
import { setTransferFlavor } from "../constants";
import {
  safeCapture,
  safeGroup,
  safeIdentify,
  safeRegister,
} from "../utils/posthog";
import { config } from "../config";
import { carryOverClientSessionFacts } from "../utils/cipFlow";

export function useClaimLink() {
  const { linkId: linkIdFromParams } = useParams<{ linkId: string; flow: string }>();
  const [searchParams] = useSearchParams();
  const linkIdFromQuery = searchParams.get("linkId");
  
  // Obtener linkId SOLO de la URL (path params > queryParams)
  const linkId = linkIdFromParams || linkIdFromQuery;
  
  const { session, setSession, setError } = useSessionStore();
  const setPartnerInfo = usePartnerStore((state) => state.setPartnerInfo);
  const { setLinkId } = useStore();
  const { t, i18n } = useTranslation();
  const isLoadingRef = useRef(false);

  useEffect(() => {
    // Si no hay linkId en ningún lado, mostrar error
    if (!linkId) {
      setError({
        title: t("error.invalidLink"),
        subTitle: null,
        message: t("error.tokenMissing"),
        btnText: null,
        isOut: false,
        errorCode: LINK_ERROR_CODE.INVALID_TOKEN,
      });
      return;
    }

    // Solo hacer claim si NOT_LOADED (nunca se ha intentado)
    if (session.status === RequestStatus.NOT_LOADED && !isLoadingRef.current) {
      isLoadingRef.current = true;

      (async () => {
        // Persisted data from a previous claim of this link (page reload):
        // keeps client-only facts such as "CIP already ran this session".
        const previousData = useSessionStore.getState().session.data;
        try {
          setSession({
            data: null,
            status: RequestStatus.LOADING,
            error: null,
          });

          // Collect device info
          const deviceInfo = {
            userAgent: navigator.userAgent,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };

          const sessionData = carryOverClientSessionFacts(
            previousData,
            await claimLink(linkId, deviceInfo)
          );

          // Set the link ID in global state for subsequent views
          setLinkId(sessionData.linkId);
          
          // Set the link ID for subsequent API requests (adds x-link-id header)
          setWidgetLinkId(sessionData.linkId);

          // Set i18n language from session if link has language configured
          if (
            sessionData.language &&
            ["en", "es", "fr"].includes(sessionData.language)
          ) {
            i18n.changeLanguage(sessionData.language);
          }

          // Override transfer flavor if link specifies one
          if (sessionData.flavor) {
            setTransferFlavor(sessionData.flavor);
          }

          // DOM light/dark mode is resolved in useReadTheme from useSystemTheme +
          // OS preference (or theme.lightness when useSystemTheme is false).

          // Store partner info for session expired redirect
          setPartnerInfo({
            sessionExpiredRedirectUrl:
              sessionData.partner?.sessionExpiredRedirectUrl,
            name: sessionData.partner?.name,
          });

          setSession({
            data: sessionData,
            status: RequestStatus.LOADED,
            error: null,
          });

          // PostHog: identify user and register session super-properties.
          // Called after setTransferFlavor so config.TRANSFER_FLAVOR is resolved.
          safeIdentify(sessionData.userData.referenceId, {
            partner_id: sessionData.partner.partnerId,
            partner_name: sessionData.partner.name,
            source_country: sessionData.config.sourceCountry,
            destination_country: sessionData.config.destinationCountry,
            language: sessionData.language,
          });
          safeGroup("partner", sessionData.partner.partnerId, {
            name: sessionData.partner.name,
          });
          safeRegister({
            link_id: sessionData.linkId,
            session_id: sessionData.sessionId,
            env: config.ENV_NAME,
            flavor: config.TRANSFER_FLAVOR,
          });
          safeCapture("ux.onboarding.claim.link.claimed");

          isLoadingRef.current = false;
        } catch (error: unknown) {
          isLoadingRef.current = false;

          if (error instanceof LinkClaimError) {
            let title = t("error.linkError");
            let message = error.message;

            switch (error.code) {
              case LINK_ERROR_CODE.LINK_NOT_FOUND:
                // LINK_NOT_FOUND during claim means the session/link expired or was deleted
                // Show session expired message to guide user back to the app
                title = t("error.sessionExpired");
                message = t("error.sessionExpiredMessage");
                break;
              case LINK_ERROR_CODE.LINK_EXPIRED:
                // Link expired - show session expired message
                title = t("error.sessionExpired");
                message = t("error.sessionExpiredMessage");
                break;
              case LINK_ERROR_CODE.LINK_ALREADY_USED:
                title = t("error.linkAlreadyUsed");
                message = t("error.linkAlreadyUsedMessage");
                break;
              case LINK_ERROR_CODE.INVALID_TOKEN:
                title = t("error.invalidLink");
                message = t("error.invalidTokenMessage");
                break;
            }

            setError({
              title,
              subTitle: null,
              message,
              btnText: null,
              isOut: false,
              errorCode: error.code,
            });
          } else {
            setError({
              title: t("error.unexpectedError"),
              subTitle: null,
              message: t("error.tryAgainLater"),
              btnText: null,
              isOut: false,
              errorCode: LINK_ERROR_CODE.INTERNAL_ERROR,
            });
          }
        }
      })();
    }
  }, [
    linkId,
    session.status,
    setSession,
    setError,
    setLinkId,
    setPartnerInfo,
    t,
    i18n,
  ]);
}
